import { Injectable } from "@angular/core";
import {
  HttpClient,
  HttpHeaders,
  HttpParams,
  HttpResponse,
} from "@angular/common/http";
import { Observable, of, throwError } from "rxjs";
import { catchError, tap } from "rxjs/operators";
import { ConfigService } from "../../utils/config.service";
import { CatalogSummary } from "../model/catalog-summary";
import { Catalog } from "../model/catalog";
import { FeatureSummary } from "../model/feature-summary";
import { VersionAttributes } from "../model/version-attributes";
import { CatalogItemMetadata } from "../model/catalog-item-metadata";
import { CatalogItem, Property, PropertyVersion } from "../model/catalog-item";
import { ACL } from "../model/acl";
import { Profile, ProfileSummary } from "../model/profile";
import { Ruleset, RulesetSummary } from "../model/ruleset";
import { Rule, RulePage } from "../model/rules";
import { ApiError } from "../model/api-error";
import { DataTypeDefinition } from "../model/data-type";

@Injectable({
  providedIn: "root",
})
export class DataCatalogueService {
  private apiServer = ConfigService.settings.catalogsApiServer;
  private catalogsUrl = `${this.apiServer.url}/catalogs`;
  // private catalogsUrl = `http://192.168.10.114:8080/fcatalog-be/catalogs`;

  constructor(private http: HttpClient) {}
  listCatalogs(): Observable<CatalogSummary[]> {
    return this.http.get<CatalogSummary[]>(this.catalogsUrl).pipe(
      tap((data) => console.log("getCatalogs response:", data)),
      catchError(this.handleHttpError<CatalogSummary[]>("listCatalogs", [])),
    );
  }

  getCatalogMetadata(catId: string): Observable<Catalog> {
    const url = `${this.catalogsUrl}/${catId}`;
    return this.http.get<Catalog>(url).pipe(
      tap((data) => console.log(`getCatalogById(${catId}) response:`, data)),
      catchError(this.handleHttpError<Catalog>(`getCatalogMetadata`)),
    );
  }

  getCatalogData(catId: string): Observable<HttpResponse<Blob>> {
    const url = `${this.catalogsUrl}/${catId}/download`;
    return this.http
      .get(url, { responseType: "blob", observe: "response" })
      .pipe(
        tap((response) => {
          const contentDisposition = response.headers.get(
            "Content-Disposition",
          );
          console.log("Suggested filename:", contentDisposition);
        }),
        catchError(this.handleHttpError<HttpResponse<Blob>>("getCatalogData")),
      );
  }
  /*  getCatalogData(catId: string): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/${catId}/download`, { responseType: 'blob' })
      .pipe(catchError(this.handleHttpError<Blob>('getCatalogData')));
  }*/

  getFeatures(catId: string): Observable<FeatureSummary[]> {
    const url = `${this.catalogsUrl}/${catId}/features`;

    const headers = new HttpHeaders({
      "Content-Type": "application/json",
      "Access-Control-Allow-Origin": "*",
    });

    return this.http.get<FeatureSummary[]>(url, { headers }).pipe(
      tap((data) => console.log(`getFeatures(${catId}) response:`, data)),
      catchError(this.handleHttpError<FeatureSummary[]>(`getFeatures`, [])),
    );
  }

  getItemMetadata(
    catId: string,
    itemType: string,
    ns: string,
    itemId: string,
  ): Observable<CatalogItem> {
    const url = `${this.catalogsUrl}/${catId}/${itemType}/${ns}/${itemId}`;
    return this.http
      .get<CatalogItem>(url)
      .pipe(catchError(this.handleHttpError<CatalogItem>(`getItemMetadata`)));
  }

  updateItemMetadata(
    catId: string,
    itemType: string,
    ns: string,
    itemId: string,
    metadata: CatalogItemMetadata,
  ): Observable<VersionAttributes> {
    const url = `${this.catalogsUrl}/${catId}/${itemType}/${ns}/${itemId}`;
    return this.http
      .patch<VersionAttributes>(url, metadata)
      .pipe(
        catchError(
          this.handleHttpError<VersionAttributes>(`updateItemMetadata`),
        ),
      );
  }

  getItemCount(
    catId: string,
    itemType: string,
    ns: string,
    itemId: string,
  ): Observable<number> {
    const url = `${this.catalogsUrl}/${catId}/${itemType}/${ns}/${itemId}/stats/count`;
    return this.http
      .get<number>(url)
      .pipe(catchError(this.handleHttpError<number>(`getItemCount`, 0)));
  }

  getItemACL(
    catId: string,
    itemType: string,
    ns: string,
    itemId: string,
  ): Observable<ACL> {
    const url = `${this.catalogsUrl}/${catId}/${itemType}/${ns}/${itemId}/acl`;
    return this.http
      .get<ACL>(url)
      .pipe(catchError(this.handleHttpError<ACL>(`getItemACL`)));
  }

  updateItemACL(
    catId: string,
    itemType: string,
    ns: string,
    itemId: string,
    acl: ACL,
  ): Observable<VersionAttributes> {
    const url = `${this.catalogsUrl}/${catId}/${itemType}/${ns}/${itemId}/acl`;
    return this.http
      .patch<VersionAttributes>(url, acl)
      .pipe(
        catchError(this.handleHttpError<VersionAttributes>(`updateItemACL`)),
      );
  }

  getDataType(
    catId: string,
    ns: string,
    itemId: string,
  ): Observable<DataTypeDefinition> {
    const url = `${this.catalogsUrl}/${catId}/dataTypes/${ns}/${itemId}`;
    return this.http
      .get<DataTypeDefinition>(url)
      .pipe(
        catchError(this.handleHttpError<DataTypeDefinition>("getDataType")),
      );
  }

  getEnumACL(
    catId: string,
    ns: string,
    itemId: string,
    enumValue: string,
  ): Observable<ACL> {
    const url = `${this.catalogsUrl}/${catId}/dataTypes/${ns}/${itemId}/enums/${enumValue}/acl`;
    return this.http
      .get<ACL>(url)
      .pipe(catchError(this.handleHttpError<ACL>("getEnumACL", [])));
  }

  updateEnumACL(
    catId: string,
    ns: string,
    itemId: string,
    enumValue: string,
    acl: ACL,
  ): Observable<VersionAttributes> {
    const url = `${this.catalogsUrl}/${catId}/dataTypes/${ns}/${itemId}/enums/${enumValue}/acl`;
    return this.http
      .patch<VersionAttributes>(url, acl)
      .pipe(
        catchError(this.handleHttpError<VersionAttributes>("updateEnumACL")),
      );
  }

  saveEnumeration(
    catId: string,
    ns: string,
    itemId: string,
    enumValue: string,
    data: any,
  ): Observable<VersionAttributes> {
    const url = `${this.catalogsUrl}/${catId}/dataTypes/${ns}/${itemId}/enums/${enumValue}`;
    return this.http
      .put<VersionAttributes>(url, data)
      .pipe(
        catchError(this.handleHttpError<VersionAttributes>("saveEnumeration")),
      );
  }
  createOrUpdateEnum(
    catId: string,
    ns: string,
    itemId: string,
    enumValue: string,
    body: any,
  ): Observable<any> {
    const url = `${this.catalogsUrl}/${catId}/dataTypes/${ns}/${itemId}/enums/${enumValue}`;

    return this.http.put(url, body);
  }
  deleteEnumeration(
    catId: string,
    ns: string,
    itemId: string,
    enumValue: string,
  ): Observable<void> {
    if (!enumValue) {
      console.warn("deleteEnumeration: empty enumValue");
      return throwError(() => new Error("enumValue is required"));
    }

    const url = `${this.catalogsUrl}/${catId}/dataTypes/${ns}/${itemId}/enums/${encodeURIComponent(enumValue)}`;

    return this.http
      .delete<void>(url)
      .pipe(catchError(this.handleHttpError<void>("deleteEnumeration")));
  }

  getPropertyMetadata(catId: string, propertyId: string): Observable<Property> {
    const url = `${this.catalogsUrl}/${catId}/property/${propertyId}`;
    return this.http
      .get<Property>(url)
      .pipe(
        catchError(
          this.handleHttpError<Property>(`getPropertyMetadata(${propertyId})`),
        ),
      );
  }

  updatePropertyMetadata(
    catId: string,
    propertyId: string,
    body: PropertyVersion,
  ): Observable<VersionAttributes> {
    const url = `${this.catalogsUrl}/${catId}/property/${propertyId}`;
    return this.http
      .patch<VersionAttributes>(url, body)
      .pipe(
        catchError(
          this.handleHttpError<VersionAttributes>(
            `updatePropertyMetadata(${propertyId})`,
          ),
        ),
      );
  }

  getPropertyCount(catId: string, propertyId: string): Observable<number> {
    const url = `${this.catalogsUrl}/${catId}/property/${propertyId}/stats/count`;
    return this.http
      .get<number>(url)
      .pipe(
        catchError(
          this.handleHttpError<number>(`getPropertyCount(${propertyId})`, 0),
        ),
      );
  }
  /** GET ACL za property */
  getPropertyACL(catId: string, propertyId: string): Observable<ACL> {
    const url = `${this.catalogsUrl}/${catId}/property/${propertyId}/acl`;
    return this.http.get<ACL>(url).pipe(
      tap((data) =>
        console.log(`getPropertyACL(${propertyId}) response:`, data),
      ),
      catchError(this.handleHttpError<ACL>(`getPropertyACL(${propertyId})`)),
    );
  }

  /** PATCH update ACL za property */
  updatePropertyACL(
    catId: string,
    propertyId: string,
    acl: ACL,
  ): Observable<VersionAttributes> {
    const url = `${this.catalogsUrl}/${catId}/property/${propertyId}/acl`;
    return this.http.patch<VersionAttributes>(url, acl).pipe(
      tap((data) =>
        console.log(`updatePropertyACL(${propertyId}) response:`, data),
      ),
      catchError(
        this.handleHttpError<VersionAttributes>(
          `updatePropertyACL(${propertyId})`,
        ),
      ),
    );
  }

  /** GET /catalogs/{catId}/profiles — lista profila */
  getProfiles(catId: string): Observable<Profile[]> {
    const url = `${this.catalogsUrl}/${catId}/profiles`;
    return this.http
      .get<Profile[]>(url) //vraca niz profila
      .pipe(catchError(this.handleHttpError<Profile[]>("getProfiles", [])));
  }

  /** POST /catalogs/{catId}/profiles — kreira novi profil */
  createProfile(catId: string, profile: Profile): Observable<Profile> {
    const url = `${this.catalogsUrl}/${catId}/profiles`;
    return this.http
      .post<Profile>(url, profile)
      .pipe(catchError(this.handleHttpError<Profile>("createProfile")));
  }

  /** PATCH /catalogs/{catId}/profiles/{profileId} — ažurira postojeći profil */
  updateProfile(
    catId: string,
    profileId: string,
    data: ProfileSummary,
  ): Observable<Profile> {
    const url = `${this.catalogsUrl}/${catId}/profiles/${profileId}`;
    return this.http
      .patch<Profile>(url, data)
      .pipe(catchError(this.handleHttpError<Profile>("updateProfile")));
  }

  /** DELETE /catalogs/{catId}/profiles/{profileId} — briše profil */
  deleteProfile(catId: string, profileId: string): Observable<void> {
    const url = `${this.catalogsUrl}/${catId}/profiles/${profileId}`;
    return this.http
      .delete<void>(url)
      .pipe(catchError(this.handleHttpError<void>("deleteProfile")));
  }

  /** GET /catalogs/{catId}/profiles/{profileId}/download — preuzima katalog za dati profil */
  downloadProfileCatalog(catId: string, profileId: string): Observable<Blob> {
    const url = `${this.catalogsUrl}/${catId}/profiles/${profileId}/download`;
    return this.http
      .get(url, { responseType: "blob" })
      .pipe(catchError(this.handleHttpError<Blob>("downloadProfileCatalog")));
  }

  getRulesets(catId: string): Observable<Ruleset[]> {
    return this.http
      .get<Ruleset[]>(`${this.catalogsUrl}/${catId}/rulesets`)
      .pipe(catchError(this.handleHttpError<Ruleset[]>("getRulesets", [])));
  }

  createRuleset(catId: string, ruleset: RulesetSummary): Observable<Ruleset> {
    return this.http
      .post<Ruleset>(`${this.catalogsUrl}/${catId}/rulesets`, ruleset)
      .pipe(catchError(this.handleHttpError<Ruleset>("createRuleset")));
  }

  updateRuleset(
    catId: string,
    rulesetId: string,
    ruleset: RulesetSummary,
  ): Observable<Ruleset> {
    return this.http
      .patch<Ruleset>(
        `${this.catalogsUrl}/${catId}/rulesets/${rulesetId}`,
        ruleset,
      )
      .pipe(catchError(this.handleHttpError<Ruleset>("updateRuleset")));
  }

  deleteRuleset(catId: string, rulesetId: string): Observable<void> {
    return this.http
      .delete<void>(`${this.catalogsUrl}/${catId}/rulesets/${rulesetId}`)
      .pipe(catchError(this.handleHttpError<void>("deleteRuleset")));
  }
  /** GET /catalogs/{catId}/rules */
  getRules(
    catId: string,
    q?: string,
    pageSize: number = 50,
    pageIndex: number = 0,
  ): Observable<RulePage> {
    let params = new HttpParams()
      .set("pageSize", pageSize)
      .set("pageIndex", pageIndex);
    if (q) params = params.set("q", q);

    return this.http
      .get<RulePage>(`${this.catalogsUrl}/${catId}/rules`, { params })
      .pipe(catchError(this.handleHttpError<RulePage>("getRules")));
  }

  /** POST /catalogs/{catId}/rules */
  createRule(catId: string, rule: Rule): Observable<Rule> {
    return this.http
      .post<Rule>(`${this.catalogsUrl}/${catId}/rules`, rule)
      .pipe(catchError(this.handleHttpError<Rule>("createRule")));
  }

  /** GET /catalogs/{catId}/rules/{ruleId} */
  getRule(catId: string, ruleId: string): Observable<Rule> {
    return this.http
      .get<Rule>(`${this.catalogsUrl}/${catId}/rules/${ruleId}`)
      .pipe(catchError(this.handleHttpError<Rule>("getRule")));
  }

  /** PATCH /catalogs/{catId}/rules/{ruleId} */
  updateRule(catId: string, ruleId: string, rule: Rule): Observable<Rule> {
    return this.http
      .patch<Rule>(`${this.catalogsUrl}/${catId}/rules/${ruleId}`, rule)
      .pipe(catchError(this.handleHttpError<Rule>("updateRule")));
  }

  /** DELETE /catalogs/{catId}/rules/{ruleId} */
  deleteRule(catId: string, ruleId: string): Observable<void> {
    return this.http
      .delete<void>(`${this.catalogsUrl}/${catId}/rules/${ruleId}`)
      .pipe(catchError(this.handleHttpError<void>("deleteRule")));
  }

  private handleHttpError<T>(operation = "operation", defaultValue?: T) {
    return (error: any): Observable<T> => {
      let apiError: any | undefined;

      if (error.error && error.error.code && error.error.message) {
        apiError = error.error as any;
      }

      console.error(`${operation} failed:`, apiError ?? error);
      if (error.status === 401 || error.status === 403) {
        console.warn("Unauthorized or forbidden access");
        // ovde može redirect na login ili toast poruku
      } else if (error.status === 404) {
        console.warn("Resource not found");
      } else if (error.status >= 500) {
        console.error("Server error");
      }

      return defaultValue !== undefined
        ? of(defaultValue as T)
        : throwError(() => apiError ?? error);
    };
  }
}
